import type { OrderSummaryBasket } from '@/components/order-summary/mobile-heading-utils';
import type { SCAPIShopperBasketsV2Schemas } from '@/types/scapi';
import { getSoldByWeightSurchargeAmount } from '@/lib/sold-by-weight-surcharge';

/**
 * Promotion ID used by the `dw.order.calculate` hook to tag the floor delivery
 * shipping price adjustment. Must match the value in
 * `cartridges/app_project/cartridge/scripts/helpers/floorDeliverySurcharge.js`.
 */
export const FLOOR_DELIVERY_PROMOTION_ID = 'floorDeliverySurcharge';
export const APPOINTMENT_DELIVERY_PROMOTION_ID = 'appointmentDeliverySurcharge';

/** Form field carrying the shopper's floor delivery choice through checkout. */
export const FLOOR_DELIVERY_FIELD = 'floorDelivery';
export const APPOINTMENT_DELIVERY_FIELD = 'appointmentDelivery';
export const SHIPPING_METHOD_FIELD = 'shippingMethodId';

export type DeliveryServiceSurchargeAmounts = {
    floor?: number;
    appointment?: number;
};

export function getDeliveryServiceSurchargeAmounts(
    basket: OrderSummaryBasket | undefined
): DeliveryServiceSurchargeAmounts {
    return (basket?.shippingItems ?? []).reduce<DeliveryServiceSurchargeAmounts>((amounts, shippingItem) => {
        shippingItem.priceAdjustments?.forEach((adjustment) => {
            if (typeof adjustment.price !== 'number') return;
            if (adjustment.promotionId === FLOOR_DELIVERY_PROMOTION_ID) {
                amounts.floor = (amounts.floor ?? 0) + adjustment.price;
            } else if (adjustment.promotionId === APPOINTMENT_DELIVERY_PROMOTION_ID) {
                amounts.appointment = (amounts.appointment ?? 0) + adjustment.price;
            }
        });
        return amounts;
    }, {});
}

export type DeliveryServiceOption = {
    enabled: boolean;
    surcharge: number;
    selected: boolean;
    description?: string;
};

export type ShippingMethodWithDeliveryOptions = SCAPIShopperBasketsV2Schemas['ShippingMethod'];
export type ShippingMethodResultWithDeliveryOptions = Omit<
    SCAPIShopperBasketsV2Schemas['ShippingMethodResult'],
    'applicableShippingMethods'
> & {
    applicableShippingMethods?: ShippingMethodWithDeliveryOptions[];
};
type Shipment = SCAPIShopperBasketsV2Schemas['Shipment'];

const DISABLED_SERVICE: DeliveryServiceOption = { enabled: false, surcharge: 0, selected: false };

function toDeliveryServiceOption(
    enabled: boolean | undefined,
    surcharge: number | undefined,
    selected: boolean | undefined,
    description: string | undefined
): DeliveryServiceOption {
    if (enabled !== true || typeof surcharge !== 'number' || !Number.isFinite(surcharge) || surcharge <= 0) {
        return DISABLED_SERVICE;
    }

    return {
        enabled: true,
        surcharge,
        selected: selected === true,
        description: description?.trim() || undefined,
    };
}

export function getDeliveryServiceOptions(
    shippingMethod: ShippingMethodWithDeliveryOptions | undefined,
    shipment?: Shipment
): { floor: DeliveryServiceOption; appointment: DeliveryServiceOption } {
    if (!shippingMethod) {
        return { floor: DISABLED_SERVICE, appointment: DISABLED_SERVICE };
    }

    return {
        floor: toDeliveryServiceOption(
            shippingMethod.c_floorDeliveryEnabled,
            shippingMethod.c_floorDeliveryPrice,
            shipment?.c_floorDelivery,
            shippingMethod.c_floorDeliveryDescription
        ),
        appointment: toDeliveryServiceOption(
            shippingMethod.c_appointmentDeliveryEnabled,
            shippingMethod.c_appointmentDeliveryPrice,
            shipment?.c_appointmentDelivery,
            shippingMethod.c_appointmentDeliveryDescription
        ),
    };
}

export type FloorDeliveryOption = {
    /** Whether the assigned shipping method offers floor delivery at a usable price. */
    enabled: boolean;
    /** Surcharge added on top of the shipping cost when floor delivery is chosen. */
    surcharge: number;
    /** Whether the shopper has already chosen floor delivery on this basket. */
    selected: boolean;
    /** Shipping cost without either surcharge, i.e. the price of standard delivery. */
    baseShippingTotal?: number;
};

const DISABLED: FloorDeliveryOption = { enabled: false, surcharge: 0, selected: false };

/** Sums the applied delivery-service adjustments already present on the basket. */
function getAppliedSurchargeAmount(basket: OrderSummaryBasket): number {
    if (!basket.shippingItems) {
        return 0;
    }

    return basket.shippingItems.reduce((total, shippingItem) => {
        const adjustments = shippingItem.priceAdjustments?.filter(
            (priceAdjustment) =>
                priceAdjustment.promotionId === FLOOR_DELIVERY_PROMOTION_ID ||
                priceAdjustment.promotionId === APPOINTMENT_DELIVERY_PROMOTION_ID
        );
        return (
            total +
            (adjustments?.reduce(
                (subtotal, adjustment) =>
                    typeof adjustment.price === 'number' ? subtotal + adjustment.price : subtotal,
                0
            ) ?? 0)
        );
    }, 0);
}

/**
 * Resolves the floor delivery option from the shipping method currently assigned
 * to the basket. The method is assigned server-side on every calculation, so the
 * option can appear or disappear as the basket changes.
 *
 * Returns a disabled option whenever the method does not offer floor delivery or
 * its price is missing or non-positive, so callers can hide the choice entirely
 * rather than render an option with no usable price.
 *
 * @param basket - the basket to inspect
 * @returns the floor delivery option for this basket
 */
export function getFloorDeliveryOption(basket: OrderSummaryBasket | undefined): FloorDeliveryOption {
    const shipment = basket?.shipments?.[0];
    const shippingMethod = shipment?.shippingMethod;

    if (!basket || !shippingMethod || shippingMethod.c_floorDeliveryEnabled !== true) {
        return DISABLED;
    }

    const surcharge = shippingMethod.c_floorDeliveryPrice;
    if (typeof surcharge !== 'number' || !Number.isFinite(surcharge) || surcharge <= 0) {
        return DISABLED;
    }

    // Delivery-service and sold-by-weight surcharges are positive shipping price
    // adjustments already folded into `shippingTotal`, so remove them for the base cost.
    const baseShippingTotal =
        typeof basket.shippingTotal === 'number'
            ? basket.shippingTotal - getAppliedSurchargeAmount(basket) - (getSoldByWeightSurchargeAmount(basket) ?? 0)
            : undefined;

    return {
        enabled: true,
        surcharge,
        selected: shipment?.c_floorDelivery === true,
        baseShippingTotal,
    };
}
